import React, { useState, useEffect } from 'react';
import { resolveOrgId } from './utils/authHeaders';
import { Calendar, Clock, Users, DollarSign, Bed, CheckCircle, AlertCircle, Settings } from 'lucide-react';

const RoomBooking = () => {
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid, calendar, list
  const [filters, setFilters] = useState({
    roomType: '',
    status: '',
    dateRange: null
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchRooms();
    fetchBookings();
    fetchGuests();
  }, []);

  const fetchRooms = async () => {
    try {
      const token = localStorage.getItem('token');
      const orgId = resolveOrgId();
      
      const response = await fetch(`${API_URL}/api/rooms`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRooms(data);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const orgId = resolveOrgId();
      
      const response = await fetch(`${API_URL}/api/bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchGuests = async () => {
    try {
      const token = localStorage.getItem('token');
      const orgId = resolveOrgId();
      
      const response = await fetch(`${API_URL}/api/guests`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGuests(data);
      }
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'text-green-600 bg-green-100',
      occupied: 'text-red-600 bg-red-100',
      maintenance: 'text-yellow-600 bg-yellow-100',
      cleaning: 'text-blue-600 bg-blue-100',
      reserved: 'text-purple-600 bg-purple-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const getRoomTypeIcon = (type) => {
    const icons = {
      single: <Users className="w-4 h-4" />,
      double: <Users className="w-4 h-4" />,
      suite: <Bed className="w-4 h-4" />,
      deluxe: <Settings className="w-4 h-4" />
    };
    return icons[type] || <Bed className="w-4 h-4" />;
  };

  const filteredRooms = rooms.filter(room => {
    if (filters.roomType && room.room_type !== filters.roomType) return false;
    if (filters.status && room.status !== filters.status) return false;
    return true;
  });

  const RoomCard = ({ room }) => (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
         onClick={() => { setSelectedRoom(room); setShowBookingModal(true); }}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center space-x-2">
          {getRoomTypeIcon(room.room_type)}
          <h3 className="text-lg font-semibold">Room {room.room_number}</h3>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(room.status)}`}>
          {room.status}
        </span>
      </div>
      
      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex justify-between">
          <span>Type:</span>
          <span className="font-medium capitalize">{room.room_type}</span>
        </div>
        <div className="flex justify-between">
          <span>Floor:</span>
          <span className="font-medium">{room.floor}</span>
        </div>
        <div className="flex justify-between">
          <span>Capacity:</span>
          <span className="font-medium">{room.capacity} guests</span>
        </div>
        <div className="flex justify-between">
          <span>Price:</span>
          <span className="font-medium">${room.current_price}/night</span>
        </div>
      </div>

      {room.amenities.length > 0 && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-1">
            {room.amenities.slice(0, 3).map((amenity, index) => (
              <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                {amenity}
              </span>
            ))}
            {room.amenities.length > 3 && (
              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                +{room.amenities.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const BookingModal = () => {
    const [bookingData, setBookingData] = useState({
      guest_id: '',
      check_in_date: '',
      check_out_date: '',
      adults: 1,
      children: 0,
      special_requests: ''
    });

    const handleCreateBooking = async () => {
      try {
        const token = localStorage.getItem('token');
        const orgId = resolveOrgId();
        
        const response = await fetch(`${API_URL}/api/bookings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Organization-Id': orgId
          },
          body: JSON.stringify({
            ...bookingData,
            room_id: selectedRoom.id,
            total_amount: selectedRoom.current_price
          })
        });

        if (response.ok) {
          setShowBookingModal(false);
          fetchBookings();
          setSelectedRoom(null);
        }
      } catch (error) {
        console.error('Error creating booking:', error);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <h2 className="text-2xl font-bold mb-6">Book Room {selectedRoom.room_number}</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Guest</label>
              <select aria-label="Guest" 
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={bookingData.guest_id}
                onChange={(e) => setBookingData({...bookingData, guest_id: e.target.value})}
              >
                <option value="">Select Guest</option>
                {guests.map(guest => (
                  <option key={guest.id} value={guest.id}>
                    {guest.first_name} {guest.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check In</label>
                <input aria-label="Check In" 
                  type="date" 
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={bookingData.check_in_date}
                  onChange={(e) => setBookingData({...bookingData, check_in_date: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label>
                <input aria-label="Check Out" 
                  type="date" 
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={bookingData.check_out_date}
                  onChange={(e) => setBookingData({...bookingData, check_out_date: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adults</label>
                <input aria-label="Adults" 
                  type="number" 
                  min="1"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={bookingData.adults}
                  onChange={(e) => setBookingData({...bookingData, adults: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Children</label>
                <input aria-label="Children" 
                  type="number" 
                  min="0"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={bookingData.children}
                  onChange={(e) => setBookingData({...bookingData, children: parseInt(e.target.value)})}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
              <textarea aria-label="Special Requests" 
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                rows="3"
                value={bookingData.special_requests}
                onChange={(e) => setBookingData({...bookingData, special_requests: e.target.value})}
                placeholder="Any special requests or notes..."
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button 
              onClick={() => setShowBookingModal(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button 
              onClick={handleCreateBooking}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Create Booking
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Room Booking Management</h1>
        <div className="flex space-x-2">
          <button 
            onClick={() => setViewMode('grid')}
            className={`px-4 py-2 rounded-md ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Grid
          </button>
          <button 
            onClick={() => setViewMode('calendar')}
            className={`px-4 py-2 rounded-md ${viewMode === 'calendar' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Calendar
          </button>
          <button 
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded-md ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            List
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Room Type</label>
            <select aria-label="Room Type" 
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={filters.roomType}
              onChange={(e) => setFilters({...filters, roomType: e.target.value})}
            >
              <option value="">All Types</option>
              <option value="single">Single</option>
              <option value="double">Double</option>
              <option value="suite">Suite</option>
              <option value="deluxe">Deluxe</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select aria-label="Status" 
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
            >
              <option value="">All Status</option>
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="maintenance">Maintenance</option>
              <option value="cleaning">Cleaning</option>
              <option value="reserved">Reserved</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <input aria-label="Date Range" 
              type="date" 
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-full">
              <Bed className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Rooms</p>
              <p className="text-2xl font-semibold">{rooms.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-full">
              <CheckCircle className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Available</p>
              <p className="text-2xl font-semibold">
                {rooms.filter(r => r.status === 'available').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-full">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Occupied</p>
              <p className="text-2xl font-semibold">
                {rooms.filter(r => r.status === 'occupied').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-full">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Today's Revenue</p>
              <p className="text-2xl font-semibold">
                ${bookings.reduce((sum, b) => sum + (b.payment_status === 'paid' ? b.total_amount : 0), 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Rooms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredRooms.map(room => (
          <RoomCard key={room.id} room={room} />
        ))}
      </div>

      {showBookingModal && <BookingModal />}
    </div>
  );
};

export default RoomBooking;
